import { useState } from 'react';
import { ItemMaster } from '../../../services/apiTypes';
import {
  MANAGE_ITEM_TEMP_ID,
  MANAGE_ITEM_MODE,
} from '../../../_constants/manageConst';
import { isAdmin } from '../../../_utils/utils';
import { isGetItemMaster } from '../../../_utils/typeGuardUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { API_CODE } from '../../../_constants/apiConst';
import { getAutoInputAttributes, isEmptyData } from '../../../_utils/utils';
import { formatNumberValue } from '../../../_utils/inspectionUtils';
import {
  deleteItemMaster,
  getItemMaster,
  postItemMaster,
  putItemMaster,
} from '../../../services/api/itemMaster';
import {
  DECIMAL_MAX_DIGITS,
  INTEGER_MAX_DIGITS,
} from '../../../_constants/inspectionConst';

export const defaultItem = {
  categoryId: '',
  typeId: '',
  itemId: '',
  displayName: '',
  itemName: '',
  itemNameJp: '',
  itemName03: '',
  itemName04: '',
  itemName05: '',
  itemName06: '',
  itemName07: '',
  itemName08: '',
  itemName09: '',
  itemName10: '',
  depth: 0,
  inputType: 0,
  parentItemId: null,
  criteria: {
    text: '',
    min: null,
    max: null,
  },
  sort: 0,
  isOptionalAddition: false,
  selectedAttribute1: [],
  selectedAttribute2: [],
  selectedAttribute3: [],
  selectedAttribute5: [],
  selectedAttribute6: [],
  selectedBrandName: [],
  selectedConditions: '',
  pCode: '',
  fCode: '',
  rCodeList: [],
  memo: '',
};

type useItemProps = {
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  setDisplayMode: React.Dispatch<React.SetStateAction<number>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenAccordions: Record<string, boolean>;
  handleAccordion: (id: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenMessageModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDisabledControlBtn: React.Dispatch<React.SetStateAction<boolean>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string) => void;
};

const useItem = ({
  itemMaster,
  setItemMaster,
  setDisplayMode,
  setIsOpenDeleteModal,
  isOpenAccordions,
  handleAccordion,
  setIsLoading,
  setIsOpenMessageModal,
  setIsDisabledControlBtn,
  showApiError,
  showTypeError,
}: useItemProps) => {
  const { userData } = useSelector((state: RootState) => state.userInfo);
  /** 操作対象の小項目 */
  const [targetItem, setTargetItem] = useState<ItemMaster>(defaultItem);
  /** 小項目（小項目名）編集モーダルの開閉状態 */
  const [isOpenItemEditModal, setIsOpenItemEditModal] = useState(false);

  /** 操作対象小項目をリセットする */
  const resetTargetItem = () => {
    setTargetItem(defaultItem);
  };

  /** 中項目行の新規追加ボタン押下（小項目階層0追加） */
  const onClickTypeAddIcon = (categoryId: string, typeId: string) => {
    const newItem = {
      ...defaultItem,
      categoryId,
      typeId,
      itemId: MANAGE_ITEM_TEMP_ID.ITEM,
      depth: 0,
    };
    setItemMaster([...itemMaster, newItem]);
    setDisplayMode(MANAGE_ITEM_MODE.ADD);
    setTargetItem(newItem);
    if (!isOpenAccordions[typeId]) {
      handleAccordion(typeId);
    }
  };

  /** ユーザー情報からアイテム名の言語を取得する */
  const getItemNameLang = () => {
    switch (userData.language) {
      case 'en':
        return 'itemName';
      case 'ja':
        return 'itemNameJp';
      case 'lang3':
        return 'itemName03';
      case 'lang4':
        return 'itemName04';
      case 'lang5':
        return 'itemName05';
      case 'lang6':
        return 'itemName06';
      case 'lang7':
        return 'itemName07';
      case 'lang8':
        return 'itemName08';
      case 'lang9':
        return 'itemName09';
      case 'lang10':
        return 'itemName10';
      default:
        return 'displayName';
    }
  };

  /** 小項目行の新規追加ボタン押下（小項目階層1,2追加） */
  const onClickItemAddIcon = (item: ItemMaster) => {
    // 挿入するindexを探す
    const items = itemMaster.filter((i) => i.parentItemId === item.itemId);
    let lastIndex = 0;
    if (items.length > 0) {
      let lastItemId = items[items.length - 1].itemId;
      if (item.depth === 0) {
        const children = itemMaster.filter(
          (i) => i.parentItemId === lastItemId,
        );
        lastItemId =
          children.length > 0
            ? children[children.length - 1].itemId
            : lastItemId;
      }
      lastIndex = itemMaster.findIndex((i) => i.itemId === lastItemId);
    } else {
      lastIndex = itemMaster.findIndex((i) => i.itemId === item.itemId);
    }
    const newItem = {
      ...defaultItem,
      categoryId: item.categoryId,
      typeId: item.typeId,
      itemId: MANAGE_ITEM_TEMP_ID.ITEM,
      parentItemId: item.itemId,
      depth: item.depth + 1,
    };
    setItemMaster((prev) => {
      const newItems = [...prev];
      newItems.splice(lastIndex + 1, 0, newItem);
      return newItems;
    });
    setDisplayMode(MANAGE_ITEM_MODE.ADD);
    setTargetItem(newItem);
    if (!isOpenAccordions[item.itemId]) {
      handleAccordion(item.itemId);
    }
  };

  /** 小項目の編集アイコン押下 */
  const onClickItemEditIcon = (item: ItemMaster) => {
    setTargetItem(item);
    setDisplayMode(MANAGE_ITEM_MODE.EDIT);
  };

  /** 小項目の削除アイコン押下 */
  const onClickItemDeleteIcon = (item: ItemMaster) => {
    setTargetItem(item);
    setIsOpenDeleteModal(true);
  };

  /** 削除処理実行 */
  const executeDeleteItem = async () => {
    setIsOpenDeleteModal(false);
    setIsLoading(true);
    try {
      await deleteItemMaster({ itemId: targetItem.itemId });
      // 画面を更新
      const response = await getItemMaster();
      if (!isGetItemMaster(response)) {
        showTypeError(API_CODE.ITEM_MASTER);
      } else {
        setItemMaster(response.itemList);
      }
    } catch (error) {
      showApiError(error, API_CODE.ITEM_MASTER);
    } finally {
      resetTargetItem();
      setIsLoading(false);
    }
  };

  /** 小項目の編集行入力時の処理 */
  const handleItemInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    item: ItemMaster,
  ) => {
    const { name, value } = e.target;
    switch (name) {
      case 'displayName':
        return {
          ...item,
          displayName: value,
          [getItemNameLang()]: value,
        };
      case 'inputType':
        return { ...item, [name]: value ? 1 : 0 };

      case 'criteriaText':
        return {
          ...item,
          criteria: { ...item.criteria, ['text']: value },
        };
      case 'criteriaMin':
        const minString = formatNumberValue(
          value,
          INTEGER_MAX_DIGITS,
          DECIMAL_MAX_DIGITS,
        );
        return {
          ...item,
          criteria: {
            ...item.criteria,
            ['min']: minString === '' ? null : Number(minString),
          },
        };
      case 'criteriaMax':
        const maxString = formatNumberValue(
          value,
          INTEGER_MAX_DIGITS,
          DECIMAL_MAX_DIGITS,
        );
        return {
          ...item,
          criteria: {
            ...item.criteria,
            ['max']: maxString === '' ? null : Number(maxString),
          },
        };
      default:
        return { ...item, [name]: value };
    }
  };

  /** 属性の入力チェックと判定条件の表示制御 */
  const checkInputForCondition = (item: ItemMaster) => {
    // 複数の属性(カラム)に属性がある場合は条件(AND,OR)を必須にする
    let countAttr = 0;
    if (item.selectedAttribute1.length > 0) {
      countAttr += 1;
    }
    if (item.selectedAttribute2.length > 0) {
      countAttr += 1;
    }
    if (item.selectedAttribute3.length > 0) {
      countAttr += 1;
    }
    if (item.selectedAttribute5.length > 0) {
      countAttr += 1;
    }
    if (item.selectedAttribute6.length > 0) {
      countAttr += 1;
    }
    if (item.selectedBrandName.length > 0) {
      countAttr += 1;
    }
    return countAttr < 2;
  };

  /** 小項目の編集内容を確定 */
  const onClickItemEditSave = async (item: ItemMaster) => {
    setIsDisabledControlBtn(true);
    setTargetItem(item);
    let isMessage = false;

    if (item.itemId === MANAGE_ITEM_TEMP_ID.ITEM) {
      // 新規追加
      // 入力チェック(属性)
      const updateItems = checkInputAttributes(item);
      isMessage = !isSetPFRCodes(item);
      try {
        setIsLoading(true);
        const postItem = {
          ...item,
          itemName: item.displayName,
          isOptionalAddition: isAdmin(userData)
            ? item.isOptionalAddition
            : true,
        };
        await postItemMaster(postItem);
        if (updateItems && updateItems.length > 0) {
          await Promise.all(
            updateItems.map(async (item) => {
              if (item.itemId) {
                await putItemMaster(item);
              }
            }),
          );
        }

        // 小項目再取得
        const response = await getItemMaster();
        if (!isGetItemMaster(response)) {
          showTypeError(API_CODE.ITEM_MASTER);
        }
        setItemMaster(response.itemList);
        resetTargetItem();
        setDisplayMode(MANAGE_ITEM_MODE.VIEW);
        // PFRのメッセージがあれば表示する
        if (isMessage) {
          setIsOpenMessageModal(true);
        }
      } catch (error) {
        showApiError(error, API_CODE.ITEM_MASTER);
      } finally {
        setIsLoading(false);
      }
    } else {
      // 編集
      // 入力チェック(属性)
      const updateItems = checkInputAttributes(item);
      // 最下層の項目の場合はPFR入力チェック
      if (
        itemMaster.filter((i) => i.parentItemId === item.itemId).length === 0
      ) {
        isMessage = !isSetPFRCodes(item);
      }
      try {
        setIsLoading(true);
        await putItemMaster(item);
        if (updateItems && updateItems.length > 0) {
          await Promise.all(
            updateItems.map(async (item) => {
              if (item.itemId) {
                await putItemMaster(item);
              }
            }),
          );
        }
        // 小項目再取得
        const response = await getItemMaster();
        if (!isGetItemMaster(response)) {
          showTypeError(API_CODE.ITEM_MASTER);
        }
        setItemMaster(response.itemList);
        resetTargetItem();
        setDisplayMode(MANAGE_ITEM_MODE.VIEW);
        // PFRのメッセージがあれば表示する
        if (isMessage) {
          setIsOpenMessageModal(true);
        }
      } catch (error) {
        showApiError(error, API_CODE.ITEM_MASTER);
      } finally {
        setIsLoading(false);
      }
    }
    setIsDisabledControlBtn(false);
  };

  /** 小項目の編集キャンセル */
  const onClickItemEditCancel = () => {
    if (targetItem.itemId === MANAGE_ITEM_TEMP_ID.ITEM) {
      setItemMaster(
        itemMaster.filter((i) => i.itemId !== MANAGE_ITEM_TEMP_ID.ITEM),
      );
    }
    resetTargetItem();
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
  };

  /** 小項目名の編集モーダルを開く */
  const onClickItemNamesEdit = (item: ItemMaster) => {
    setTargetItem(item);
    setIsOpenItemEditModal(true);
  };

  /** 小項目名の編集モーダル保存 */
  const handleItemNamesSave = (item: ItemMaster) => {
    setTargetItem({ ...item, displayName: item[getItemNameLang()] });
    setIsOpenItemEditModal(false);
  };

  /** 小項目名の編集モーダルキャンセル */
  const handleItemNamesCancel = () => {
    setIsOpenItemEditModal(false);
  };

  /** PFRが入力済みかを判定する */
  const isSetPFRCodes = (item: ItemMaster) => {
    return (
      !isEmptyData(item.pCode) &&
      !isEmptyData(item.fCode) &&
      item.rCodeList.length > 0
    );
  };

  /** 属性入力チェック処理 */
  const checkInputAttributes = (item: ItemMaster) => {
    // 編集中以外で変更のある項目を格納する
    let updateItems: ItemMaster[] = [];

    if (item.depth === 0) {
      // 階層0
      const depth0Item = item;
      const depth1Items = itemMaster.filter(
        (i) => i.parentItemId === depth0Item.itemId,
      );
      if (depth1Items) {
        // 子項目があれば、属性の自動入力を行う
        updateItems = getAttrItemsDepth0(depth0Item, depth1Items, updateItems);
      }
    } else if (item.depth === 1) {
      // 階層1
      const depth1Item = item;
      const depth0Item =
        itemMaster.find((i) => i.itemId === item.parentItemId) ?? defaultItem;
      // 属性の自動入力を行う
      updateItems = getAttrItemsDepth1(depth0Item, depth1Item, updateItems);
    } else {
      // 階層2
      const depth2Item = item;
      const depth1Item =
        itemMaster.find((i) => i.itemId === item.parentItemId) ?? defaultItem;
      const depth0Item =
        itemMaster.find((i) => i.itemId === depth1Item?.parentItemId) ??
        defaultItem;
      // 属性の自動入力を行う
      updateItems = getAttrItemsDepth2(
        depth0Item,
        depth1Item,
        depth2Item,
        updateItems,
      );
    }
    return updateItems;
  };

  /** 階層0の属性入力チェック */
  const getAttrItemsDepth0 = (
    depth0Item: ItemMaster,
    depth1Items: ItemMaster[],
    updateItems: ItemMaster[],
  ) => {
    // 階層0と1、階層1と2の順で親子の入力チェックを行う
    depth1Items.map((item1) => {
      /** 階層0と1 */
      // 属性1
      const attr1Upper = getAutoInputAttributes(
        depth0Item.selectedAttribute1,
        item1.selectedAttribute1,
      );
      depth0Item.selectedAttribute1 = attr1Upper.parent;
      item1.selectedAttribute1 = attr1Upper.child;
      // 属性2
      const attr2Upper = getAutoInputAttributes(
        depth0Item.selectedAttribute2,
        item1.selectedAttribute2,
      );
      depth0Item.selectedAttribute2 = attr2Upper.parent;
      item1.selectedAttribute2 = attr2Upper.child;
      // 属性3
      const attr3Upper = getAutoInputAttributes(
        depth0Item.selectedAttribute3,
        item1.selectedAttribute3,
      );
      depth0Item.selectedAttribute3 = attr3Upper.parent;
      item1.selectedAttribute3 = attr3Upper.child;
      // 属性5
      const attr5Upper = getAutoInputAttributes(
        depth0Item.selectedAttribute5,
        item1.selectedAttribute5,
      );
      depth0Item.selectedAttribute5 = attr5Upper.parent;
      item1.selectedAttribute5 = attr5Upper.child;
      // 属性6
      const attr6Upper = getAutoInputAttributes(
        depth0Item.selectedAttribute6,
        item1.selectedAttribute6,
      );
      depth0Item.selectedAttribute6 = attr6Upper.parent;
      item1.selectedAttribute6 = attr6Upper.child;
      // ブランド名
      const brandUpper = getAutoInputAttributes(
        depth0Item.selectedBrandName,
        item1.selectedBrandName,
      );
      depth0Item.selectedBrandName = brandUpper.parent;
      item1.selectedBrandName = brandUpper.child;
      // 判定条件
      if (depth0Item.selectedConditions) {
        item1.selectedConditions = depth0Item.selectedConditions;
      }
      /** 階層1と2 */
      const depth2Items = itemMaster.filter(
        (i) => i.parentItemId === item1.itemId,
      );
      if (depth2Items) {
        depth2Items.map((item2) => {
          // 属性1
          const attr1Lower = getAutoInputAttributes(
            item1.selectedAttribute1,
            item2.selectedAttribute1,
          );
          item1.selectedAttribute1 = attr1Lower.parent;
          item2.selectedAttribute1 = attr1Lower.child;
          // 属性2
          const attr2Lower = getAutoInputAttributes(
            item1.selectedAttribute2,
            item2.selectedAttribute2,
          );
          item1.selectedAttribute2 = attr2Lower.parent;
          item2.selectedAttribute2 = attr2Lower.child;
          // 属性3
          const attr3Lower = getAutoInputAttributes(
            item1.selectedAttribute3,
            item2.selectedAttribute3,
          );
          item1.selectedAttribute3 = attr3Lower.parent;
          item2.selectedAttribute3 = attr3Lower.child;
          // 属性5
          const attr5Lower = getAutoInputAttributes(
            item1.selectedAttribute5,
            item2.selectedAttribute5,
          );
          item1.selectedAttribute5 = attr5Lower.parent;
          item2.selectedAttribute5 = attr5Lower.child;
          // 属性6
          const attr6Lower = getAutoInputAttributes(
            item1.selectedAttribute6,
            item2.selectedAttribute6,
          );
          item1.selectedAttribute6 = attr6Lower.parent;
          item2.selectedAttribute6 = attr6Lower.child;
          // ブランド名
          const brandLower = getAutoInputAttributes(
            item1.selectedBrandName,
            item2.selectedBrandName,
          );
          item1.selectedBrandName = brandLower.parent;
          item2.selectedBrandName = brandLower.child;
          // 判定条件
          if (item1.selectedConditions) {
            item2.selectedConditions = item1.selectedConditions;
          }

          updateItems.push(item2);
        });
        updateItems.push(item1);
      } else {
        updateItems.push(item1);
      }
    });
    setTargetItem(depth0Item);
    return updateItems;
  };

  /** 階層1の属性入力チェック */
  const getAttrItemsDepth1 = (
    depth0Item: ItemMaster,
    depth1Item: ItemMaster,
    updateItems: ItemMaster[],
  ) => {
    /** 階層0と1 */
    // 属性1
    const attr1Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute1,
      depth1Item.selectedAttribute1,
    );
    depth0Item.selectedAttribute1 = attr1Upper.parent;
    depth1Item.selectedAttribute1 = attr1Upper.child;
    // 属性2
    const attr2Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute2,
      depth1Item.selectedAttribute2,
    );
    depth0Item.selectedAttribute2 = attr2Upper.parent;
    depth1Item.selectedAttribute2 = attr2Upper.child;
    // 属性3
    const attr3Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute3,
      depth1Item.selectedAttribute3,
    );
    depth0Item.selectedAttribute3 = attr3Upper.parent;
    depth1Item.selectedAttribute3 = attr3Upper.child;
    // 属性5
    const attr5Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute5,
      depth1Item.selectedAttribute5,
    );
    depth0Item.selectedAttribute5 = attr5Upper.parent;
    depth1Item.selectedAttribute5 = attr5Upper.child;
    // 属性6
    const attr6Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute6,
      depth1Item.selectedAttribute6,
    );
    depth0Item.selectedAttribute6 = attr6Upper.parent;
    depth1Item.selectedAttribute6 = attr6Upper.child;
    // ブランド名
    const brandUpper = getAutoInputAttributes(
      depth0Item.selectedBrandName,
      depth1Item.selectedBrandName,
    );
    depth0Item.selectedBrandName = brandUpper.parent;
    depth1Item.selectedBrandName = brandUpper.child;
    // 判定条件
    if (depth0Item.selectedConditions) {
      depth1Item.selectedConditions = depth0Item.selectedConditions;
    }

    updateItems.push(depth0Item);

    /** 階層1と2 */
    const children = itemMaster.filter(
      (i) => i.parentItemId === depth1Item.itemId,
    );
    if (children) {
      children.map((childItem) => {
        // 属性1
        const attr1Lower = getAutoInputAttributes(
          depth1Item.selectedAttribute1,
          childItem.selectedAttribute1,
        );
        depth1Item.selectedAttribute1 = attr1Lower.parent;
        childItem.selectedAttribute1 = attr1Lower.child;
        // 属性2
        const attr2Lower = getAutoInputAttributes(
          depth1Item.selectedAttribute2,
          childItem.selectedAttribute2,
        );
        depth1Item.selectedAttribute2 = attr2Lower.parent;
        childItem.selectedAttribute2 = attr2Lower.child;
        // 属性3
        const attr3Lower = getAutoInputAttributes(
          depth1Item.selectedAttribute3,
          childItem.selectedAttribute3,
        );
        depth1Item.selectedAttribute3 = attr3Lower.parent;
        childItem.selectedAttribute3 = attr3Lower.child;
        // 属性5
        const attr5Lower = getAutoInputAttributes(
          depth1Item.selectedAttribute5,
          childItem.selectedAttribute5,
        );
        depth1Item.selectedAttribute5 = attr5Lower.parent;
        childItem.selectedAttribute5 = attr5Lower.child;
        // 属性6
        const attr6Lower = getAutoInputAttributes(
          depth1Item.selectedAttribute6,
          childItem.selectedAttribute6,
        );
        depth1Item.selectedAttribute6 = attr6Lower.parent;
        childItem.selectedAttribute6 = attr6Lower.child;
        // ブランド名
        const brandLower = getAutoInputAttributes(
          depth1Item.selectedBrandName,
          childItem.selectedBrandName,
        );
        depth1Item.selectedBrandName = brandLower.parent;
        childItem.selectedBrandName = brandLower.child;
        // 判定条件
        if (depth1Item.selectedConditions) {
          childItem.selectedConditions = depth1Item.selectedConditions;
        }

        updateItems.push(childItem);
      });
    }
    setTargetItem(depth1Item);
    return updateItems;
  };

  /** 階層2の属性入力チェック */
  const getAttrItemsDepth2 = (
    depth0Item: ItemMaster,
    depth1Item: ItemMaster,
    depth2Item: ItemMaster,
    updateItems: ItemMaster[],
  ) => {
    /** 階層1と2 */
    // 属性1
    const attr1Lower = getAutoInputAttributes(
      depth1Item.selectedAttribute1,
      depth2Item.selectedAttribute1,
    );
    depth1Item.selectedAttribute1 = attr1Lower.parent;
    depth2Item.selectedAttribute1 = attr1Lower.child;
    // 属性2
    const attr2Lower = getAutoInputAttributes(
      depth1Item.selectedAttribute2,
      depth2Item.selectedAttribute2,
    );
    depth1Item.selectedAttribute2 = attr2Lower.parent;
    depth2Item.selectedAttribute2 = attr2Lower.child;
    // 属性3
    const attr3Lower = getAutoInputAttributes(
      depth1Item.selectedAttribute3,
      depth2Item.selectedAttribute3,
    );
    depth1Item.selectedAttribute3 = attr3Lower.parent;
    depth2Item.selectedAttribute3 = attr3Lower.child;
    // 属性5
    const attr5Lower = getAutoInputAttributes(
      depth1Item.selectedAttribute5,
      depth2Item.selectedAttribute5,
    );
    depth1Item.selectedAttribute5 = attr5Lower.parent;
    depth2Item.selectedAttribute5 = attr5Lower.child;
    // 属性6
    const attr6Lower = getAutoInputAttributes(
      depth1Item.selectedAttribute6,
      depth2Item.selectedAttribute6,
    );
    depth1Item.selectedAttribute6 = attr6Lower.parent;
    depth2Item.selectedAttribute6 = attr6Lower.child;
    // ブランド名
    const brandLower = getAutoInputAttributes(
      depth1Item.selectedBrandName,
      depth2Item.selectedBrandName,
    );
    depth1Item.selectedBrandName = brandLower.parent;
    depth2Item.selectedBrandName = brandLower.child;

    /** 階層0と1 */
    // 属性1
    const attr1Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute1,
      depth1Item.selectedAttribute1,
    );
    depth0Item.selectedAttribute1 = attr1Upper.parent;
    depth1Item.selectedAttribute1 = attr1Upper.child;
    // 属性2
    const attr2Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute2,
      depth1Item.selectedAttribute2,
    );
    depth0Item.selectedAttribute2 = attr2Upper.parent;
    depth1Item.selectedAttribute2 = attr2Upper.child;
    // 属性3
    const attr3Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute3,
      depth1Item.selectedAttribute3,
    );
    depth0Item.selectedAttribute3 = attr3Upper.parent;
    depth1Item.selectedAttribute3 = attr3Upper.child;
    // 属性5
    const attr5Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute5,
      depth1Item.selectedAttribute5,
    );
    depth0Item.selectedAttribute5 = attr5Upper.parent;
    depth1Item.selectedAttribute5 = attr5Upper.child;
    // 属性6
    const attr6Upper = getAutoInputAttributes(
      depth0Item.selectedAttribute6,
      depth1Item.selectedAttribute6,
    );
    depth0Item.selectedAttribute6 = attr6Upper.parent;
    depth1Item.selectedAttribute6 = attr6Upper.child;
    // ブランド名
    const brandUpper = getAutoInputAttributes(
      depth0Item.selectedBrandName,
      depth1Item.selectedBrandName,
    );
    depth0Item.selectedBrandName = brandUpper.parent;
    depth1Item.selectedBrandName = brandUpper.child;
    // 判定条件
    if (depth0Item.selectedConditions) {
      // 階層0に判定条件があれば階層1,2にコピーする
      depth1Item.selectedConditions = depth0Item.selectedConditions;
      depth2Item.selectedConditions = depth0Item.selectedConditions;
    } else if (depth1Item.selectedConditions) {
      // 階層1に判定条件があれば階層2にコピーする
      depth2Item.selectedConditions = depth1Item.selectedConditions;
    }

    updateItems.push(depth0Item);
    updateItems.push(depth1Item);
    setTargetItem(depth2Item);
    return updateItems;
  };

  return {
    targetItem,
    isOpenItemEditModal,
    resetTargetItem,
    onClickTypeAddIcon,
    onClickItemAddIcon,
    onClickItemEditIcon,
    onClickItemDeleteIcon,
    executeDeleteItem,
    handleItemInputChange,
    checkInputForCondition,
    onClickItemEditSave,
    onClickItemEditCancel,
    onClickItemNamesEdit,
    handleItemNamesSave,
    handleItemNamesCancel,
    isSetPFRCodes,
  };
};

export default useItem;
